// =============================================================================
// NEWSLETTER SIGNUP COMPONENT
// =============================================================================

import React, { useState } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useToastNotifications } from '@/hooks/useToastNotifications';
import type { BaseComponentProps } from '@/types';

interface NewsletterSignupProps extends BaseComponentProps {
  variant?: 'default' | 'minimal' | 'footer';
  title?: string;
  description?: string;
  placeholder?: string;
  buttonText?: string;
  successMessage?: string;
  errorMessage?: string;
  showIcon?: boolean;
  showDescription?: boolean;
  className?: string;
  onSuccess?: (email: string) => void;
  onError?: (error: string) => void;
}

interface NewsletterResponse {
  success: boolean;
  message: string;
  email?: string;
  alreadySubscribed?: boolean;
}

export const NewsletterSignup: React.FC<NewsletterSignupProps> = ({
  variant = 'default',
  title = 'Stay Updated',
  description = 'Get the latest updates and insights delivered to your inbox.',
  placeholder = 'Enter your email address',
  buttonText = 'Subscribe',
  successMessage = 'Thank you for subscribing!',
  errorMessage = 'Something went wrong. Please try again.',
  showIcon = true,
  showDescription = true,
  className = '',
  onSuccess,
  onError,
}) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inputError, setInputError] = useState(false);
  const { showSuccess, showError } = useToastNotifications();

  const subscribeToNewsletter = functions ? httpsCallable<{ email: string; source?: string }, NewsletterResponse>(
    functions,
    'subscribeToNewsletter'
  ) : null;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setInputError(true);
      showError('Please enter your email address');
      onError?.('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setInputError(true);
      showError('Please enter a valid email address');
      onError?.('Please enter a valid email address');
      return;
    }

    if (!subscribeToNewsletter) {
      showError('Newsletter service is not available');
      onError?.('Newsletter service is not available');
      return;
    }

    setIsLoading(true);
    setInputError(false);

    try {
      const result = await subscribeToNewsletter({
        email: email.trim(),
        source: 'website'
      });

      if (result.data.success) {
        const message = result.data.alreadySubscribed 
          ? 'You are already subscribed to our newsletter'
          : successMessage;
        showSuccess(message);
        onSuccess?.(result.data.email || email);
        setEmail(''); // Clear form on success
      } else {
        showError(result.data.message || errorMessage);
        onError?.(result.data.message || errorMessage);
      }
    } catch (error) {
      // Newsletter subscription error
      showError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear input error when user starts typing
    if (inputError) {
      setInputError(false);
    }
  };


  const getVariantClasses = () => {
    switch (variant) {
      case 'minimal':
        return 'newsletter-signup--minimal';
      case 'footer':
        return 'newsletter-signup--footer';
      default:
        return 'newsletter-signup--default';
    }
  };

  return (
    <div className={`newsletter-signup ${getVariantClasses()} ${className}`}>
      {(variant === 'default' || variant === 'minimal') && (
        <div className="newsletter-header">
          {showIcon && (
            <div className="newsletter-icon">
              <Mail className="w-6 h-6" />
            </div>
          )}
          <h3 className="newsletter-title">{title}</h3>
          {showDescription && (
            <p className="newsletter-description">{description}</p>
          )}
        </div>
      )}

      {variant === 'footer' && (
        <div className="newsletter-header--footer">
          <h4 className="newsletter-title--footer">{title}</h4>
          {showDescription && (
            <p className="newsletter-description--footer">{description}</p>
          )}
        </div>
      )}

      <form className="newsletter-form" onSubmit={handleSubmit}>
        <div className="newsletter-form-group">
          <div className="newsletter-input-wrapper">
            <input
              type="email"
              value={email}
              onChange={handleInputChange}
              placeholder={placeholder}
              disabled={isLoading}
              className={`newsletter-input ${inputError ? 'error' : ''}`}
              required
            />
            {showIcon && variant !== 'footer' && (
              <Mail className="newsletter-input-icon" />
            )}
          </div>
          <button
            type="submit"
            disabled={isLoading || !email.trim()}
            className="newsletter-submit"
          >
            {isLoading ? (
              <>
                <Loader2 className="newsletter-spinner" />
                Subscribing...
              </>
            ) : (
              buttonText
            )}
          </button>
        </div>

      </form>

      {variant === 'default' && (
        <p className="newsletter-disclaimer">
          We respect your privacy. Unsubscribe at any time.
        </p>
      )}
    </div>
  );
};

export default NewsletterSignup;
